import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const SecurityDashboard = () => {
  const navigate = useNavigate();
  const [securityUser, setSecurityUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('verify');

  useEffect(() => {
    // Check if security user is logged in
    const user = JSON.parse(localStorage.getItem('securityUser') || 'null');
    if (!user || user.role !== 'security') {
      navigate('/security/login');
      return;
    }
    setSecurityUser(user);
    loadSecurityLogs();
  }, [navigate]);

  const loadSecurityLogs = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/security/logs`);
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      }
    } catch (error) {
      console.error('Error loading security logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('securityUser');
    navigate('/security/login');
  };

  const getStatusBadge = (dropoffVerified, pickupVerified) => {
    if (dropoffVerified && pickupVerified) {
      return <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Complete</span>;
    } else if (dropoffVerified || pickupVerified) {
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">Partial</span>;
    } else {
      return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">Pending</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-8">
        <div className="text-center">Loading security dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-blue-900">Security Dashboard</h1>
          {securityUser && (
            <div className="flex items-center mt-2">
              <span className="text-gray-600 mr-2">Welcome, {securityUser.name}</span>
              <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
                Security Personnel
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={loadSecurityLogs}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-300"
          >
            Refresh
          </button>
          <button
            onClick={handleLogout}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition duration-300"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <div className="text-2xl font-bold text-blue-600">{logs.length}</div>
          <div className="text-sm text-gray-600">Total Students Today</div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <div className="text-2xl font-bold text-green-600">
            {logs.filter(l => l.dropoff_verified && l.pickup_verified).length}
          </div>
          <div className="text-sm text-gray-600">Complete Attendance</div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <div className="text-2xl font-bold text-yellow-600">
            {logs.filter(l => l.dropoff_verified || l.pickup_verified).length}
          </div>
          <div className="text-sm text-gray-600">Partial Attendance</div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <div className="text-2xl font-bold text-red-600">
            {logs.filter(l => !l.dropoff_verified && !l.pickup_verified).length}
          </div>
          <div className="text-sm text-gray-600">No Verification</div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-blue-800">Today's Security Verifications</h2>
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('verify')}
              className={`px-4 py-2 rounded ${activeTab === 'verify' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              Code Verification
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`px-4 py-2 rounded ${activeTab === 'logs' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              View Logs
            </button>
          </div>
        </div>

        {activeTab === 'verify' && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">Security</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Code Verification</h3>
            <p className="text-gray-600 mb-6">Parents and guardians enter verification codes here</p>
            <a
              href="/security"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition duration-300 font-medium"
            >
              Open Verification Page ->
            </a>
          </div>
        )}

        {activeTab === 'logs' && (
          <div>
            {logs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No security codes generated for today.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dropoff</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pickup</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {logs.map((log) => (
                      <tr key={log.id}>
                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                          {log.student_name}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                          {log.class_name}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                          {log.dropoff_verified ? (
                            <div>
                              <span className="text-green-600">? Verified</span>
                              {log.dropoff_verified_at && (
                                <div className="text-xs text-gray-400">
                                  {new Date(log.dropoff_verified_at).toLocaleTimeString()}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                          {log.pickup_verified ? (
                            <div>
                              <span className="text-green-600">? Verified</span>
                              {log.pickup_verified_at && (
                                <div className="text-xs text-gray-400">
                                  {new Date(log.pickup_verified_at).toLocaleTimeString()}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          {getStatusBadge(log.dropoff_verified, log.pickup_verified)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
        <h3 className="text-lg font-semibold text-blue-800 mb-4">Security Personnel Guidelines</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-blue-700 mb-2">Daily Setup:</h4>
            <ul className="text-sm text-blue-600 space-y-1">
              <li>? Ensure admin has generated today's codes</li>
              <li>? Verify verification page is accessible</li>
              <li>? Check system connectivity</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-blue-700 mb-2">During Operations:</h4>
            <ul className="text-sm text-blue-600 space-y-1">
              <li>? Monitor verification logs in real-time</li>
              <li>? Assist parents with code entry if needed</li>
              <li>? Report any system issues to admin</li>
              <li>? Ensure only authorized personnel access codes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityDashboard;





